import { useState, useEffect } from 'react';
import { catchError, tap } from "rxjs/operators";
import { forkJoin } from "rxjs";
import { HubsLogbook } from 'Models';
import { HubsLogbookService } from 'Services';
import { useSnackbar } from "notistack";

type HubsLogbooksState = { hubsLogbooks: HubsLogbook[], isLoading: boolean }

export function useHubsLogbook(id: number) {
    const [hubsLogbook, setHubsLogbook] = useState<HubsLogbook>(new HubsLogbook());
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        const observable = forkJoin({
            hubsLogbook: HubsLogbookService.GetById(id)
        });
        observable.pipe(
            tap(response => {
                setHubsLogbook(response.hubsLogbook);
            }),
            catchError(error => {
                setHubsLogbook(new HubsLogbook());
                enqueueSnackbar(error.toString(), {variant: 'error'});
                return Promise.reject(error);
            })
        ).subscribe();
    }, [enqueueSnackbar, id]);
    return { hubsLogbook, setHubsLogbook };
}

export function useAllHubsLogbooks() {
    const [hubsLogbooksState, setHubsLogbooksState] = useState<HubsLogbooksState>({ hubsLogbooks: [], isLoading: true });
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        const observable = forkJoin({
            hubsLogbooks: HubsLogbookService.GetAll()
        });
        observable.pipe(
            tap(response => {
                setHubsLogbooksState({ hubsLogbooks: response.hubsLogbooks, isLoading: false });
            }),
            catchError(error => {
                setHubsLogbooksState({ hubsLogbooks: [], isLoading: false });
                enqueueSnackbar(error.toString(), {variant: 'error'});
                return Promise.reject(error);
            })
        ).subscribe();
    }, [enqueueSnackbar]);
    return { hubsLogbooks: hubsLogbooksState.hubsLogbooks, setHubsLogbooksState, isLoading: hubsLogbooksState.isLoading };
}

export function useAllHubsLogbooksByStatusName(status: string) {
    const [hubsLogbooksState, setHubsLogbooksState] = useState<HubsLogbooksState>({ hubsLogbooks: [], isLoading: true });
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        const observable = forkJoin({
            hubsLogbooks: HubsLogbookService.GetAllByStatusName(status)
        });
        observable.pipe(
            tap(response => {
                setHubsLogbooksState({ hubsLogbooks: response.hubsLogbooks, isLoading: false });
            }),
            catchError(error => {
                setHubsLogbooksState({ hubsLogbooks: [], isLoading: false });
                enqueueSnackbar(error.toString(), {variant: 'error'});
                return Promise.reject(error);
            })
        ).subscribe();
    }, [enqueueSnackbar, status]);
    return { hubsLogbooks: hubsLogbooksState.hubsLogbooks, setHubsLogbooksState, isLoading: hubsLogbooksState.isLoading };
}

export function useStatuses() {
    const [statuses, setStatuses] = useState<string[]>([]);
    const {enqueueSnackbar} = useSnackbar();
    useEffect(() => {
        const observable = forkJoin({
            statuses: HubsLogbookService.GetAllUniqueStatuses()
        });
        observable.pipe(
            tap(response => {
                setStatuses(response.statuses);
            }),
            catchError(error => {
                enqueueSnackbar(error.toString(), {variant: 'error'});
                return Promise.reject(error);
            })
        ).subscribe();
    }, [enqueueSnackbar]);
    return { statuses, setStatuses };
}